import { Outlet, Link, useNavigate } from "@tanstack/react-router";
import { BarChart3, Calendar, CalendarDays, DoorOpen, LayoutDashboard, LogOut, MapPin, Package, UserCog, Users } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Button } from "../components/Button";
import { t } from "../locales/de";
import { cn } from "../lib/cn";

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
};

type NavGroup = {
  label: string;
  items: NavItem[];
};

const navGroups: NavGroup[] = [
  {
    label: "Planen",
    items: [
      { to: "/", label: t.nav.dashboard, icon: LayoutDashboard },
      { to: "/calendar", label: t.nav.calendar, icon: Calendar },
      { to: "/sessions", label: t.nav.sessions, icon: CalendarDays },
    ],
  },
  {
    label: "Auswerten",
    items: [{ to: "/reports", label: t.nav.reports, icon: BarChart3 }],
  },
  {
    label: "Stammdaten",
    items: [
      { to: "/locations", label: t.nav.locations, icon: MapPin },
      { to: "/rooms", label: t.nav.rooms, icon: DoorOpen },
      { to: "/resources", label: t.nav.resources, icon: Package },
      { to: "/trainers", label: t.nav.trainers, icon: UserCog },
    ],
  },
  {
    label: "Administration",
    items: [{ to: "/users", label: t.nav.users, icon: Users, adminOnly: true }],
  },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  async function onLogout() {
    await logout();
    navigate({ to: "/login" });
  }

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900">
      <aside className="flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-5">
          <h1 className="text-base font-semibold tracking-tight text-slate-950">{t.appName}</h1>
          <p className="mt-0.5 text-xs text-slate-500">{t.appTagline}</p>
        </div>

        <nav aria-label="Hauptnavigation" className="flex-1 overflow-y-auto px-3 py-4">
          {navGroups.map((group) => {
            const items = group.items.filter((item) => !item.adminOnly || user?.role === "admin");
            if (!items.length) return null;
            return <section key={group.label} className="mb-5 last:mb-0"><h2 className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{group.label}</h2><div className="space-y-1">{items.map((item) => <Link key={item.to} to={item.to} className={cn("flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-1", "")} activeProps={{ className: "flex items-center gap-3 rounded-lg bg-teal-700 px-3 py-2 text-sm font-medium text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-1" }}><item.icon aria-hidden="true" className="h-4 w-4" />{item.label}</Link>)}</div></section>;
          })}
        </nav>

        <div className="space-y-3 border-t border-slate-200 p-4">
          <div className="px-1 text-xs"><div className="truncate font-medium text-slate-800">{user?.fullName}</div><div className="mt-0.5 truncate text-slate-500">{user?.email}</div><div className="mt-1 text-slate-500">{user?.role ? t.role[user.role] : ""}</div></div>
          <Button variant="outline" size="sm" className="w-full" onClick={onLogout}><LogOut className="h-4 w-4" />{t.nav.logout}</Button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-auto">
        <div className="min-h-full p-4 md:p-6 lg:p-8"><Outlet /></div>
      </main>
    </div>
  );
}
